import type { AccountBroadcastHandler, AccountBroadcastPayload } from "@mixitone/oom";
import { getDeviceTokenFromAccessToken, oom, shouldIgnoreSelfBroadcast } from "@mixitone/oom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { isTestMode } from "lib/environment";
import tauri from ".";

/** Align with OOM broadcast coalesce window so bulk writes trigger one native sync. */
const SYNC_WAKE_DEBOUNCE_MS = 75;

export type PerformSyncFn = () => Promise<unknown>;

/**
 * Listens for account Realtime broadcasts and triggers the native debounced sync
 * worker when remote changes arrive. Self-originated events (matching device_token)
 * are ignored on native device sessions.
 */
export class TauriBroadcastSyncWake {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private removeHandler: (() => void) | null = null;
  private authUnsubscribe: (() => void) | null = null;
  private clientDeviceToken: string | null = null;

  constructor(
    private supabase: SupabaseClient,
    private performSyncFn: PerformSyncFn = () => tauri.performSync(),
  ) {}

  wire(): () => void {
    void this.updateDeviceToken();
    const { data } = this.supabase.auth.onAuthStateChange(async () => {
      await this.updateDeviceToken();
    });
    this.authUnsubscribe = () => data.subscription.unsubscribe();

    const manager = oom.accountBroadcast;
    if (!manager) {
      return () => this.unwire();
    }

    const handler: AccountBroadcastHandler = (payload) => {
      this.handleBroadcast(payload);
    };
    this.removeHandler = manager.addHandler(handler);

    return () => this.unwire();
  }

  unwire() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.removeHandler?.();
    this.removeHandler = null;
    this.authUnsubscribe?.();
    this.authUnsubscribe = null;
  }

  handleBroadcast(payload: AccountBroadcastPayload) {
    if (!this.wakeEnabled()) {
      return;
    }
    if (shouldIgnoreSelfBroadcast(null, this.clientDeviceToken, payload)) {
      return;
    }
    this.scheduleSyncWake();
  }

  /**
   * Playwright disables the periodic native sync. An always-on wake would pull
   * remote SQL writes into other Tauri specs mid-assertion, so test builds
   * wake only when the realtime spec opts in. Production always wakes.
   */
  private wakeEnabled() {
    if (!isTestMode()) {
      return true;
    }
    if (typeof window === "undefined") {
      return false;
    }
    return window.__MIXITONE_ENABLE_TAURI_BROADCAST_SYNC__ === true;
  }

  private async updateDeviceToken() {
    const { data } = await this.supabase.auth.getSession();
    this.clientDeviceToken = getDeviceTokenFromAccessToken(data.session?.access_token);
  }

  private scheduleSyncWake() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.performSyncFn();
    }, SYNC_WAKE_DEBOUNCE_MS);
  }
}

export function wireTauriBroadcastSyncWake(supabase: SupabaseClient): () => void {
  if (!tauri.enabled) {
    return () => {};
  }
  return new TauriBroadcastSyncWake(supabase).wire();
}
